import { useEffect, useState } from "react";
import { masterApi, apiError, formatPhone } from "@/lib/api";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { modal } from "@/lib/modal";
import { Send, Save, RefreshCw, ChevronLeft, ChevronRight, Wallet, Signal, MessageSquareText } from "lucide-react";

const inputCls = "w-full h-10 rounded-lg bg-white border border-neutral-200 px-3 text-sm outline-none focus:border-[color:var(--brand-blue)] focus:ring-2 focus:ring-blue-500/15 transition";

const TIPOS_LABEL = {
  otp: "Código (OTP)", indicacao: "Indicação", campanha: "Campanha",
  teste: "Teste", manual: "Envio manual", geral: "Geral", oleo_lembrete: "Lembrete Troca de Óleo",
};

const TABS = [["envio", "Envio único"], ["integracao", "Integração"], ["historico", "Histórico"]];

export default function MasterSms() {
  const [tab, setTab] = useState("envio");
  return (
    <div className="px-4 md:px-6 pt-4 pb-8 space-y-4 float-in" data-testid="master-sms">
      <div>
        <div className="chip mb-1.5 text-[10px] py-0.5">SMS · Mensageria</div>
        <h1 className="font-serif-display text-2xl md:text-3xl leading-[1.1]">SMS</h1>
      </div>

      <div className="flex gap-1 p-1 bg-white/80 border border-neutral-200/70 rounded-full w-fit overflow-x-auto max-w-full">
        {TABS.map(([k, lbl]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`sms-tab-${k}`}
                  className={`px-3 md:px-4 h-8 rounded-full text-[12px] font-medium transition-colors whitespace-nowrap ${
                    tab === k ? "bg-neutral-900 text-white" : "text-neutral-700 hover:bg-neutral-100"
                  }`}>
            {lbl}
          </button>
        ))}
      </div>

      {tab === "envio" && <EnvioUnico />}
      {tab === "integracao" && <Integracao />}
      {tab === "historico" && <Historico />}
    </div>
  );
}

function EnvioUnico() {
  const [tipo, setTipo] = useState("sumidos");
  const [dias, setDias] = useState(30);
  const [items, setItems] = useState([]);
  const [mensagem, setMensagem] = useState(
    "Grupo Lacador: sentimos sua falta, {nome}! Volte ao posto e aproveite seus descontos de fidelidade.");
  const [sending, setSending] = useState(null); // cliente_id em envio

  const load = async () => {
    const params = new URLSearchParams({ tipo });
    if (tipo === "sumidos") params.set("dias", String(dias));
    try {
      const { data } = await masterApi.get(`/master/clientes-insights?${params.toString()}`);
      setItems(data);
    } catch (e) { modal.error(apiError(e)); }
  };
  useEffect(() => { load(); }, [tipo, dias]);

  const enviar = async (c) => {
    if (!mensagem.trim()) return modal.error("Escreva a mensagem antes de enviar");
    setSending(c.id);
    try {
      const msg = mensagem.replaceAll("{nome}", (c.nome || "").split(" ")[0]);
      await masterApi.post("/master/sms/enviar", { telefone: c.telefone, mensagem: msg });
      modal.success(`SMS enviado para ${c.nome}`);
    } catch (e) { modal.error(apiError(e)); }
    finally { setSending(null); }
  };

  const chars = mensagem.replaceAll("{nome}", "Fulano").length;

  return (
    <div className="space-y-3" data-testid="sms-envio-section">
      <div className="card-soft rounded-xl p-4 space-y-3">
        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-neutral-500">Segmento</label>
            <select data-testid="sms-tipo" value={tipo} onChange={(e) => setTipo(e.target.value)} className={inputCls + " mt-1 w-56"}>
              <option value="sumidos">Clientes Sumidos</option>
              <option value="primeiro_nao_voltou">1º Cadastro sem retorno</option>
              <option value="frequentes">Frequentes</option>
              <option value="saudade_enviado">Saudade já enviado</option>
            </select>
          </div>
          {tipo === "sumidos" && (
            <div>
              <label className="text-[10px] uppercase tracking-widest text-neutral-500">Dias</label>
              <input data-testid="sms-dias" type="number" value={dias} onChange={(e) => setDias(parseInt(e.target.value) || 30)}
                     className={inputCls + " mt-1 w-24"} />
            </div>
          )}
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-neutral-500">
            Mensagem (use {"{nome}"} p/ o primeiro nome) — <span className={chars > 160 ? "text-rose-600 font-semibold" : ""}>{chars}/160</span>
          </label>
          <textarea data-testid="sms-mensagem" value={mensagem} onChange={(e) => setMensagem(e.target.value)} rows={2}
                    className="mt-1 w-full rounded-lg bg-white border border-neutral-200 px-3 py-2 text-sm outline-none focus:border-[color:var(--brand-blue)] focus:ring-2 focus:ring-blue-500/15 transition" />
          <p className="text-[10px] text-neutral-500 mt-1">Acentos são removidos no envio; acima de 160 caracteres a mensagem é cortada.</p>
        </div>
      </div>

      <div className="card-soft rounded-xl overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-neutral-50">
              <TableHead className="text-[10px] uppercase tracking-widest">Nome</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Telefone</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Placa</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Último</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Abast.</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((c) => (
              <TableRow key={c.id} data-testid={`sms-row-${c.id}`}>
                <TableCell className="font-medium">{c.nome}</TableCell>
                <TableCell>{formatPhone(c.telefone)}</TableCell>
                <TableCell>{c.placa}</TableCell>
                <TableCell className="text-xs">{c.ultimo_abastecimento_em ? new Date(c.ultimo_abastecimento_em).toLocaleDateString("pt-BR") : "—"}</TableCell>
                <TableCell>{c.total_abastecimentos || 0}</TableCell>
                <TableCell>
                  <button data-testid={`sms-send-${c.id}`} onClick={() => enviar(c)} disabled={sending === c.id}
                          className="btn-primary-blue h-8 px-3 text-[11px] font-medium flex items-center gap-1 disabled:opacity-50">
                    <Send size={11} /> {sending === c.id ? "Enviando..." : "Enviar SMS"}
                  </button>
                </TableCell>
              </TableRow>
            ))}
            {items.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-neutral-500 py-6">Nenhum cliente no segmento.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function Integracao() {
  const [status, setStatus] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [password, setPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [telTeste, setTelTeste] = useState("");
  const [sending, setSending] = useState(false);

  const load = async () => {
    try {
      const [{ data: st }, { data: c }] = await Promise.all([
        masterApi.get("/master/sms/status"),
        masterApi.get("/master/sms/config"),
      ]);
      setStatus(st); setCfg(c);
    } catch (e) { modal.error(apiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const salvar = async () => {
    setSaving(true);
    try {
      await masterApi.put("/master/sms/config", { ...cfg, password });
      modal.success("Configuração de SMS salva");
      setPassword("");
      load();
    } catch (e) { modal.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const testar = async () => {
    if (!telTeste.trim()) return modal.error("Informe o telefone de teste");
    setSending(true);
    try {
      const { data } = await masterApi.post("/master/sms/teste", { telefone: telTeste });
      modal.success(`SMS de teste enviado (ref ${String(data.ref_id).slice(0, 8)}…)`);
    } catch (e) { modal.error(apiError(e)); }
    finally { setSending(false); }
  };

  if (!cfg) return <div className="text-sm text-neutral-500">Carregando…</div>;
  const set = (k, v) => setCfg({ ...cfg, [k]: v });

  return (
    <div className="space-y-3" data-testid="sms-integracao-section">
      {/* STATUS + SALDOS */}
      <div className="card-soft rounded-xl p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Signal size={15} className={status?.detalhe === "ok" ? "text-emerald-600" : "text-neutral-400"} />
            <h3 className="font-serif-display text-base">Status da integração (Allcance SMS)</h3>
          </div>
          <button onClick={load} className="btn-ghost h-8 px-3 text-[11px] font-medium flex items-center gap-1.5">
            <RefreshCw size={12} /> Atualizar
          </button>
        </div>
        <div className="flex flex-wrap gap-2 text-[11px]">
          <span className="chip text-[10px] py-0.5" style={status?.ativo
            ? { background: "rgba(16,185,129,0.18)", color: "#047857" }
            : { background: "rgba(244,63,94,0.15)", color: "#9F1239" }}>
            {status?.ativo ? "Ativo" : "Inativo"}
          </span>
          <span className="chip text-[10px] py-0.5">{status?.configurado ? "Credenciais OK" : "Sem credenciais"}</span>
          <span className="chip text-[10px] py-0.5">{status?.detalhe === "ok" ? "Conexão OK" : (status?.detalhe || "—")}</span>
        </div>
        {status?.saldos && (
          <div className="mt-3">
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1.5 flex items-center gap-1">
              <Wallet size={11} /> Créditos por serviço
            </div>
            <div className="flex flex-wrap gap-1.5">
              {Object.entries(status.saldos).map(([svc, cred]) => (
                <span key={svc} className="chip text-[10px] py-0.5">
                  {svc}: <b className="ml-1">{String(cred).split(".")[0]}</b>
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* CONFIG */}
      <div className="card-soft rounded-xl p-4 space-y-3 max-w-xl">
        <h3 className="font-serif-display text-base">Configuração</h3>
        <label className="flex items-center justify-between gap-3 cursor-pointer">
          <span className="text-[12px] text-neutral-700"><b>Envio de SMS ativo</b></span>
          <input data-testid="sms-cfg-ativo" type="checkbox" checked={!!cfg.ativo}
                 onChange={(e) => set("ativo", e.target.checked)} className="w-5 h-5 accent-emerald-700" />
        </label>
        <Field label="Usuário (e-mail da conta Allcance)">
          <input data-testid="sms-cfg-user" value={cfg.username || ""} onChange={(e) => set("username", e.target.value)} className={inputCls} />
        </Field>
        <Field label={`Senha ${cfg.password_set ? "(preenchida — deixe vazio p/ manter)" : ""}`}>
          <input data-testid="sms-cfg-pass" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                 placeholder={cfg.password_set ? "••••••••" : "senha da conta"} className={inputCls} />
        </Field>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Rota do código (OTP)">
            <select data-testid="sms-cfg-codotp" value={cfg.cod_otp} onChange={(e) => set("cod_otp", e.target.value)} className={inputCls}>
              <option value="11">SMS Transacional (11) — recomendado</option>
              <option value="2">SMS OTP (2)</option>
              <option value="14">SMS Blend (14)</option>
            </select>
          </Field>
          <Field label="Rota das campanhas">
            <select data-testid="sms-cfg-codcamp" value={cfg.cod_campanha} onChange={(e) => set("cod_campanha", e.target.value)} className={inputCls}>
              <option value="14">SMS Blend (14)</option>
              <option value="4">SMS Massivo (4)</option>
              <option value="2">SMS OTP (2)</option>
            </select>
          </Field>
        </div>
        <Field label="Título das campanhas (aparece no painel Allcance)">
          <input data-testid="sms-cfg-titulo" value={cfg.titulo || ""} onChange={(e) => set("titulo", e.target.value)} className={inputCls} />
        </Field>
        <button data-testid="sms-cfg-salvar" onClick={salvar} disabled={saving}
                className="btn-action btn-salvar w-full">
          <Save size={15} /> {saving ? "Salvando..." : "Salvar configuração"}
        </button>
      </div>

      {/* TESTE */}
      <div className="card-soft rounded-xl p-4 space-y-2 max-w-xl">
        <h3 className="font-serif-display text-base">Teste de envio</h3>
        <div className="flex gap-1.5">
          <input data-testid="sms-teste-tel" value={telTeste} onChange={(e) => setTelTeste(e.target.value)}
                 placeholder="(62) 99999-9999" inputMode="numeric" className={inputCls} />
          <button data-testid="sms-teste-btn" onClick={testar} disabled={sending}
                  className="btn-primary-blue h-10 px-4 text-[12px] font-medium shrink-0 flex items-center gap-1.5 disabled:opacity-50">
            <Send size={13} /> {sending ? "Enviando..." : "Enviar teste"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Historico() {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [tipo, setTipo] = useState("");
  const [sucesso, setSucesso] = useState("");

  const load = async (targetPage = 1) => {
    const params = new URLSearchParams({ page: String(targetPage), page_size: "30" });
    if (tipo) params.set("tipo", tipo);
    if (sucesso === "ok") params.set("sucesso", "true");
    if (sucesso === "fail") params.set("sucesso", "false");
    try {
      const { data } = await masterApi.get(`/master/sms/logs?${params.toString()}`);
      setItems(data.items || []);
      setPage(data.page || targetPage);
      setTotalPages(data.total_pages || 0);
      setTotal(data.total || 0);
    } catch (e) { modal.error(apiError(e)); }
  };
  useEffect(() => { load(1); }, [tipo, sucesso]);

  return (
    <div className="space-y-3" data-testid="sms-historico-section">
      <div className="card-soft rounded-xl p-3 flex flex-wrap gap-3 items-end">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-neutral-500">Tipo</label>
          <select value={tipo} onChange={(e) => setTipo(e.target.value)} data-testid="sms-hist-tipo"
                  className={inputCls + " mt-1 w-44"}>
            <option value="">Todos</option>
            {Object.entries(TIPOS_LABEL).map(([k, lbl]) => (<option key={k} value={k}>{lbl}</option>))}
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-neutral-500">Status</label>
          <select value={sucesso} onChange={(e) => setSucesso(e.target.value)} data-testid="sms-hist-sucesso"
                  className={inputCls + " mt-1 w-36"}>
            <option value="">Todos</option>
            <option value="ok">Sucesso</option>
            <option value="fail">Falha</option>
          </select>
        </div>
        <div className="ml-auto text-[11px] text-neutral-500 flex items-center gap-1.5">
          <MessageSquareText size={12} /> {total} envios
        </div>
      </div>

      <div className="card-soft rounded-xl overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-neutral-50">
              <TableHead className="text-[10px] uppercase tracking-widest">Quando</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Tipo</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Telefone</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Mensagem</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Status</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Ref / Detalhe</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((m, idx) => (
              <TableRow key={m.id || idx} data-testid={`sms-hist-row-${idx}`}>
                <TableCell className="text-xs whitespace-nowrap">{m.criado_em ? new Date(m.criado_em).toLocaleString("pt-BR") : "—"}</TableCell>
                <TableCell className="text-xs">{TIPOS_LABEL[m.tipo] || m.tipo}</TableCell>
                <TableCell className="text-xs">{formatPhone(m.telefone)}</TableCell>
                <TableCell className="text-xs text-neutral-600 max-w-[260px] truncate" title={m.mensagem}>{m.mensagem || "—"}</TableCell>
                <TableCell>
                  {m.sucesso
                    ? <span className="chip text-[10px] py-0.5" style={{ background: "rgba(16,185,129,0.18)", color: "#047857" }}>OK</span>
                    : <span className="chip text-[10px] py-0.5" style={{ background: "rgba(244,63,94,0.18)", color: "#9F1239" }}>Falha</span>}
                </TableCell>
                <TableCell className="text-xs text-neutral-500 max-w-[220px] truncate" title={m.sucesso ? m.ref_id : m.detalhe}>
                  {m.sucesso ? (m.ref_id ? `${String(m.ref_id).slice(0, 12)}…` : "ok") : (m.detalhe || "—")}
                </TableCell>
              </TableRow>
            ))}
            {items.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-neutral-500 py-6">Nenhum envio.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between gap-2">
          <button data-testid="sms-hist-prev" disabled={page <= 1} onClick={() => load(page - 1)}
                  className="btn-ghost h-9 px-3 text-[12px] font-medium flex items-center gap-1.5 disabled:opacity-40">
            <ChevronLeft size={13} /> Anterior
          </button>
          <span className="text-[11px] text-neutral-600">Página <b>{page}</b> de <b>{totalPages}</b></span>
          <button data-testid="sms-hist-next" disabled={page >= totalPages} onClick={() => load(page + 1)}
                  className="btn-ghost h-9 px-3 text-[12px] font-medium flex items-center gap-1.5 disabled:opacity-40">
            Próxima <ChevronRight size={13} />
          </button>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest text-neutral-500">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
